/**
 * UserPreferencesService - Servis za upravljanje korisničkim preferencama
 * 
 * Čuva tip korisnika (fizičko lice ili civilno društvo) i izabranu organizaciju
 * u localStorage za trajno čuvanje između sesija.
 */

const STORAGE_KEY = 'ogp_user_preferences';
const INVALID_SELECTED_ROLE = 'MODERATOR';

/**
 * True kada je userType organization/institution ali nije izabran konkretan entitet.
 * @param {object|null|undefined} prefs
 * @returns {boolean}
 */
export function isIncompleteLegalSubjectPreferences(prefs) {
  if (!prefs || typeof prefs !== 'object') return false;
  const userType = prefs.userType || 'individual';
  if (userType === 'organization') {
    return !prefs.selectedOrganizationId;
  }
  if (userType === 'institution') {
    return !prefs.selectedInstitutionId;
  }
  return false;
}

class UserPreferencesService {
  /**
   * Učitava korisničke preferencije iz localStorage
   * @returns {Object} { userType: 'individual' | 'organization', selectedOrganizationId: string | null, hasSelectedType: boolean }
   */
  getPreferences() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const prefs = JSON.parse(stored);
        if (prefs.selectedRole === INVALID_SELECTED_ROLE) {
          delete prefs.selectedRole;
        }
        // Ako postoji u storage-u, znači da je korisnik eksplicitno izabrao
        prefs.hasSelectedType = true;
        return prefs;
      }
    } catch (error) {
    }

    // Vrati podrazumevane vrednosti - korisnik NIJE izabrao tip
    return {
      userType: 'individual',
      selectedOrganizationId: null,
      selectedOrganizationName: null,
      hasSelectedType: false, // BITNO: Ovo označava da korisnik NIJE eksplicitno izabrao tip
    };
  }

  /**
   * Čuva korisničke preferencije u localStorage
   * @param {Object} preferences - { userType: string, selectedOrganizationId: string | null, selectedOrganizationName: string | null }
   */
  savePreferences(preferences) {
    try {
      // Označi da je korisnik eksplicitno izabrao tip
      const prefsToSave = {
        ...preferences,
        hasSelectedType: true,
      };
      if (prefsToSave.selectedRole === INVALID_SELECTED_ROLE) {
        delete prefsToSave.selectedRole;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefsToSave));

      // Emit custom event za komponente koje slušaju promene
      window.dispatchEvent(new CustomEvent('preferencesChanged', { detail: prefsToSave }));
    } catch (error) {
    }
  }

  /**
   * Postavlja tip korisnika
   * @param {string} userType - 'individual' ili 'organization'
   */
  setUserType(userType) {
    const preferences = this.getPreferences();
    preferences.userType = userType;

    // Ako se menja na individual, obriši izabranu organizaciju
    if (userType === 'individual') {
      preferences.selectedOrganizationId = null;
      preferences.selectedOrganizationName = null;
    }

    this.savePreferences(preferences);
  }

  /**
   * Postavlja izabranu organizaciju
   * @param {string} organizationId - ID organizacije
   * @param {string} organizationName - Naziv organizacije
   */
  setSelectedOrganization(organizationId, organizationName) {
    const preferences = this.getPreferences();
    preferences.selectedOrganizationId = organizationId;
    preferences.selectedOrganizationName = organizationName;
    preferences.userType = 'organization';
    preferences.selectedInstitutionId = null;
    preferences.selectedInstitutionName = null;
    this.savePreferences(preferences);
  }

  /**
   * Postavlja izabranu instituciju
   * @param {string} institutionId - ID institucije
   * @param {string} institutionName - Naziv institucije
   */
  setSelectedInstitution(institutionId, institutionName) {
    const preferences = this.getPreferences();
    preferences.selectedInstitutionId = institutionId;
    preferences.selectedInstitutionName = institutionName;
    preferences.userType = 'institution';
    preferences.selectedOrganizationId = null;
    preferences.selectedOrganizationName = null;
    this.savePreferences(preferences);
  }

  /**
   * Proverava da li je korisnik već izabrao tip (da li treba prikazati modal)
   * @returns {boolean}
   */
  hasSelectedUserType() {
    const preferences = this.getPreferences();

    // Prvo proveri da li je korisnik uopšte eksplicitno izabrao tip
    if (!preferences.hasSelectedType) {
      return false; // Korisnik nije izabrao tip - prikaži modal
    }

    if (isIncompleteLegalSubjectPreferences(preferences)) {
      return false;
    }

    // Ako je individual i hasSelectedType je true, korisnik je izabrao tip
    return preferences.userType === 'individual'
      || preferences.userType === 'organization'
      || preferences.userType === 'institution';
  }

  /**
   * Briše sve preferencije (koristi se pri logout-u)
   */
  clearPreferences() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
    }
  }
}

// Singleton pattern
const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;

